/**
 * domains/market/engines/stateTransition/rules.ts — MER Algo Footprints v7.1H
 *
 * Deterministic rule engine for state classification.
 *
 * Each state corresponds to a named rule function. The engine evaluates all
 * rules and selects the highest-scored match. Rules are explicitly documented
 * so every state assignment is auditable from upstream signals.
 *
 * Priority (highest wins when tied):
 *   TRAP_REVERSAL_RISK > exhaustion > compression > breakout/breakdown
 *   > continuation > pullback > range > no_valid_setup
 */

import {
  MarketState, DirectionalBias, StateScoreFactors,
  PENDING_MIN_COMPOSITE,
} from './types';
import type {
  SMCResult, RegimeResult, TrapSignal, LiquidityZone,
  TrendResult, GammaResult,
} from '../../../../shared/types';

// ─── Signal extraction helpers ────────────────────────────────────────────────

export interface SignalSummary {
  // Structural
  smcBias:          'bullish' | 'bearish' | 'neutral';
  biasStrength:     'major' | 'minor' | 'moderate';
  hasBOS:           boolean;
  bosFresh:         boolean;          // BOS within last 10 candles
  hasCHOCH:         boolean;
  chochFresh:       boolean;
  dispDirection:    'bullish' | 'bearish' | 'none';
  dispStrength:     number;           // 0..1
  dispFresh:        boolean;          // dispFreshness ≥ 0.4
  sweepAndReverse:  boolean;
  hasSweep:         boolean;
  sweepReclaim:     boolean;          // sweep happened + reclaimed
  hasEqualHighs:    boolean;          // equal high zone near price
  hasEqualLows:     boolean;
  atResistance:     boolean;          // within 0.5% of resistance zone
  atSupport:        boolean;
  aboveVWAP:        boolean;
  vwapDistPct:      number;           // |price - vwap| / vwap
  extensionPct:     number;           // |price - ema21| / ema21

  // Trap / risk
  trapSeverity:     'High' | 'Medium' | 'Low' | 'None';
  trapSide:         'bull' | 'bear' | 'neutral';

  // Regime
  regimeDominant:   'meanReversion' | 'breakout' | 'trend' | 'noTrade';
  regimeConfidence: number;           // 0..100
  regimePosture:    string;

  // Trend
  trendDirection:   'bullish' | 'bearish' | 'neutral';
  trendState:       string;
  trendStrength:    number;           // 0..1
  trendMature:      boolean;         // strength ≥ 0.6 + state = 'trend'

  // Gamma (secondary — may modify score only)
  gammaRegime:      'short' | 'long' | 'neutral' | 'none';
  gammaExpansion:   boolean;

  // Setup
  setupActionable:  boolean;
  setupConfidence:  number;           // 0..100
}

const TRAP_RANK = { High: 3, Medium: 2, Low: 1, None: 0 };

export function extractSignals(
  smc:     SMCResult | null,
  regime:  RegimeResult | null,
  traps:   TrapSignal[],
  zones:   LiquidityZone[],
  trend:   TrendResult | null,
  gamma:   GammaResult | null,
  candles_len: number,
  curPrice:    number,
  setupActionable: boolean,
  setupConfidence: number,
): SignalSummary {
  const bias      = smc?.bias ?? 'neutral';
  const bos       = smc?.lastBOS;
  const choch     = smc?.lastCHOCH;
  const bosFresh  = bos  ? (candles_len - 1 - bos.candleIdx)  <= 10 : false;
  const chochFresh= choch ? (candles_len - 1 - choch.candleIdx) <= 10 : false;

  const p = curPrice;
  const vwap = regime?.curVWAP ?? null;
  const vwapDistPct = vwap ? Math.abs(p - vwap) / vwap : 0;
  const aboveVWAP   = vwap ? p > vwap : false;

  const ema21 = trend?.ema21 ?? p;
  const extensionPct = Math.abs(p - ema21) / ema21;

  // Zone proximity (within 0.5% = "at zone")
  const resistance = zones.filter(z => z.type === 'resistance' && z.distPct < 2.0);
  const support    = zones.filter(z => z.type === 'support'    && z.distPct < 2.0);
  const atResistance = resistance.some(z => z.distPct < 0.5);
  const atSupport    = support.some(z => z.distPct < 0.5);

  // Equal highs/lows proxy — multiple resistance/support zones at very similar price
  const resGroups = groupSimilarPrices(resistance.map(z => z.price), 0.003);
  const supGroups = groupSimilarPrices(support.map(z => z.price), 0.003);
  const hasEqualHighs = resGroups.some(g => g.length >= 2);
  const hasEqualLows  = supGroups.some(g => g.length >= 2);

  // Top trap
  const topTrap = traps.sort((a, b) => TRAP_RANK[b.severity] - TRAP_RANK[a.severity])[0];

  // Gamma regime (short gamma = expansion, long gamma = pinning)
  const gr = gamma?.regime ?? '';
  const gammaRegime: SignalSummary['gammaRegime'] =
    gr.includes('Short') ? 'short' :
    gr.includes('Long')  ? 'long'  :
    gamma != null        ? 'neutral' : 'none';

  const trendDir = trend?.trendDirection ?? 'neutral';

  return {
    smcBias:         bias,
    biasStrength:    (smc?.biasStrength as SignalSummary['biasStrength']) ?? 'moderate',
    hasBOS:          !!bos,
    bosFresh,
    hasCHOCH:        !!choch,
    chochFresh,
    dispDirection:   (smc?.displacementDirection ?? 'none') as SignalSummary['dispDirection'],
    dispStrength:    smc?.displacementStrength ?? 0,
    dispFresh:       (smc?.displacementFreshness ?? 0) >= 0.4,
    sweepAndReverse: smc?.sweepAndReverse ?? false,
    hasSweep:        !!smc?.recentSweep,
    sweepReclaim:    !!(smc?.recentSweep?.reclaimed),
    hasEqualHighs,
    hasEqualLows,
    atResistance,
    atSupport,
    aboveVWAP,
    vwapDistPct,
    extensionPct,
    trapSeverity:    (topTrap?.severity ?? 'None') as SignalSummary['trapSeverity'],
    trapSide:        (topTrap?.side ?? 'neutral') as SignalSummary['trapSide'],
    regimeDominant:  regime?.dominant ?? 'noTrade',
    regimeConfidence: regime?.confidence ?? 0,
    regimePosture:   regime?.posture ?? 'neutral',
    trendDirection:  trendDir === 'bullish' ? 'bullish' : trendDir === 'bearish' ? 'bearish' : 'neutral',
    trendState:      trend?.trendState ?? 'transition',
    trendStrength:   trend?.trendStrength ?? 0,
    trendMature:     (trend?.trendStrength ?? 0) >= 0.6 && trend?.trendState === 'trend',
    gammaRegime,
    gammaExpansion:  gammaRegime === 'short',
    setupActionable,
    setupConfidence,
  };
}

function groupSimilarPrices(prices: number[], tolerancePct: number): number[][] {
  const groups: number[][] = [];
  for (const p of prices) {
    const existing = groups.find(g => Math.abs(g[0] - p) / g[0] <= tolerancePct);
    if (existing) existing.push(p);
    else groups.push([p]);
  }
  return groups;
}

// ─── Score computation ────────────────────────────────────────────────────────

export function computeScoreFactors(s: SignalSummary): StateScoreFactors {
  // Structural clarity — BOS/CHOCH freshness, bias strength, displacement
  let structuralClarity = 20;
  if (s.hasBOS)    structuralClarity += s.bosFresh  ? 35 : 15;
  if (s.hasCHOCH)  structuralClarity += s.chochFresh ? 30 : 10;
  if (s.biasStrength === 'major') structuralClarity += 15;
  if (s.dispFresh && s.dispStrength >= 0.4) structuralClarity += 15;
  structuralClarity = Math.min(100, structuralClarity);

  // Liquidity clarity — sweep, equal levels, zone proximity
  let liquidityClarity = 20;
  if (s.sweepReclaim)    liquidityClarity += 30;
  else if (s.hasSweep)   liquidityClarity += 15;
  if (s.hasEqualHighs || s.hasEqualLows) liquidityClarity += 25;
  if (s.atResistance || s.atSupport)     liquidityClarity += 20;
  liquidityClarity = Math.min(100, liquidityClarity);

  // Trigger clarity — setup maturity and confidence
  const triggerClarity = s.setupActionable ? Math.min(100, 40 + s.setupConfidence * 0.6)
                       : s.setupConfidence >= 50 ? 35 : 15;

  // Trap penalty — high trap = big penalty
  const trapPenalty = s.trapSeverity === 'High'   ? 30
                    : s.trapSeverity === 'Medium' ? 15
                    : 0;

  // Extension penalty — mature trend or price far from ema21
  let extensionPenalty = 0;
  if (s.trendMature)              extensionPenalty += 15;
  if (s.extensionPct >= 0.02)     extensionPenalty += 15; // > 2% from ema21
  if (s.extensionPct >= 0.035)    extensionPenalty += 10; // > 3.5%
  extensionPenalty = Math.min(40, extensionPenalty);

  // Gamma expansion bonus (secondary — max 15 pts, cannot override structure)
  const gammaExpansionBonus = s.gammaExpansion ? 12 : 0;

  // Regime alignment
  const regimeAlignment =
    s.regimeDominant === 'trend'         ? Math.round(s.regimeConfidence * 0.8) :
    s.regimeDominant === 'breakout'      ? Math.round(s.regimeConfidence * 0.7) :
    s.regimeDominant === 'meanReversion' ? Math.round(s.regimeConfidence * 0.5) :
    0;

  const composite = Math.min(100, Math.max(0,
    (structuralClarity * 0.30) +
    (liquidityClarity  * 0.20) +
    (triggerClarity    * 0.20) +
    (regimeAlignment   * 0.20) +
    gammaExpansionBonus -
    trapPenalty -
    extensionPenalty
  ));

  return {
    structuralClarity,
    liquidityClarity,
    triggerClarity,
    trapPenalty,
    extensionPenalty,
    gammaExpansionBonus,
    regimeAlignment,
    composite: Math.round(composite),
  };
}

// ─── Named rule evaluators ────────────────────────────────────────────────────
// Each returns a score (0 = does not apply) + reasoning.
// Higher score = stronger match.

interface RuleResult {
  score:    number;
  bias:     DirectionalBias;
  rationale: string[];
}

function ruleTrapReversalRisk(s: SignalSummary): RuleResult {
  // Fires when: active high-severity trap, or sweep without reclaim, or
  // sweep-and-reverse active. This OVERRIDES naive continuation.
  const highTrap = s.trapSeverity === 'High';
  const medTrap  = s.trapSeverity === 'Medium';
  const sweep    = s.hasSweep && !s.sweepReclaim;

  if (!highTrap && !sweep && !s.sweepAndReverse) return { score: 0, bias: 'neutral', rationale: [] };

  let score = 0;
  const rationale: string[] = [];

  if (highTrap) {
    score += 70;
    rationale.push(`High-severity ${s.trapSide} trap active`);
  } else if (s.sweepAndReverse) {
    score += 65;
    rationale.push('Sweep-and-reverse pattern confirmed');
  } else if (medTrap) {
    score += 45;
    rationale.push(`Medium ${s.trapSide} trap — monitor closely`);
  }

  if (sweep) {
    score += 15;
    rationale.push('Unreclaimed liquidity sweep — continuation suspect');
  }

  // Trap opposing a strong trend gets extra weight (trend is the trap)
  if (s.trendMature && s.trapSide !== 'neutral') {
    score += 10;
    rationale.push('Mature trend with active trap — exhaustion risk amplified');
  }

  const bias: DirectionalBias = s.trapSide === 'bull' ? 'short' : s.trapSide === 'bear' ? 'long' : 'neutral';
  return { score: Math.min(100, score), bias, rationale };
}

function ruleExhaustionRisk(s: SignalSummary): RuleResult {
  // Fires when: trend is mature AND extension is high AND price is against P/D zone
  const extended   = s.extensionPct >= 0.025;
  const mature     = s.trendMature;
  const overextend = s.extensionPct >= 0.04;

  // Require BOTH maturity AND meaningful extension (>2.5%) to qualify for EXHAUSTION_RISK.
  // A mature trend without extension is still healthy — it belongs to TREND_CONTINUATION.
  if (!mature || (!extended && !overextend)) return { score: 0, bias: 'neutral', rationale: [] };

  let score = 0;
  const rationale: string[] = [];
  const bias: DirectionalBias = s.smcBias === 'bullish' ? 'long' : s.smcBias === 'bearish' ? 'short' : 'neutral';

  if (overextend) {
    score += 60;
    rationale.push(`Price extended ${(s.extensionPct * 100).toFixed(1)}% from EMA21 — fade risk`);
  } else if (extended && mature) {
    score += 45;
    rationale.push('Trend mature and extended — exhaustion zone');
  }

  if (mature) {
    score += 20;
    rationale.push('Trend strength ≥ 0.6 — late-cycle continuation risk');
  }

  if (s.gammaExpansion) {
    score += 5;
    rationale.push('Short gamma may amplify move but reversal risk is real');
  }

  return { score: Math.min(100, score), bias, rationale };
}

function ruleCompressionBreakoutPending(s: SignalSummary): RuleResult {
  // MANDATORY: bullish bias under resistance OR bearish bias above support
  // → must NOT become a naive directional setup
  const bullishUnderResistance = s.smcBias === 'bullish' && s.atResistance;
  const bearishAboveSupport    = s.smcBias === 'bearish' && s.atSupport;
  const equalLevelsNear        = s.hasEqualHighs || s.hasEqualLows;
  const compression            = s.regimeDominant === 'meanReversion' || s.trendState === 'compression';

  if (!bullishUnderResistance && !bearishAboveSupport && !equalLevelsNear && !compression) {
    return { score: 0, bias: 'neutral', rationale: [] };
  }

  let score = 0;
  const rationale: string[] = [];

  if (bullishUnderResistance) {
    score += 55;
    rationale.push('Bullish bias but at/near resistance — breakout pending, do not buy into resistance');
  }
  if (bearishAboveSupport) {
    score += 55;
    rationale.push('Bearish bias but at/near support — breakdown pending, do not short into support');
  }
  if (s.hasEqualHighs) {
    score += 20;
    rationale.push('Equal highs near price — liquidity cluster, expect expansion or rejection');
  }
  if (s.hasEqualLows) {
    score += 20;
    rationale.push('Equal lows near price — liquidity cluster, expect expansion or rejection');
  }
  if (compression) {
    score += 15;
    rationale.push('Regime compression — directional expansion pending');
  }
  if (s.gammaExpansion) {
    score += 8;  // secondary only
    rationale.push('Short gamma increases expansion probability but does not determine direction');
  }

  const bias: DirectionalBias = s.smcBias === 'bullish' ? 'long' : s.smcBias === 'bearish' ? 'short' : 'neutral';
  return { score: Math.min(100, score), bias, rationale };
}

function ruleBreakoutConfirmedLong(s: SignalSummary): RuleResult {
  // Requires: bullish BOS + displacement aligned + reclaimed resistance or sweep + no trap
  if (s.smcBias !== 'bullish' || s.trapSeverity === 'High') return { score: 0, bias: 'long', rationale: [] };
  if (!s.hasBOS || !s.bosFresh) return { score: 0, bias: 'long', rationale: [] };

  let score = 0;
  const rationale: string[] = [];

  if (s.bosFresh && s.dispDirection === 'bullish' && s.dispFresh) {
    score += 60;
    rationale.push('Fresh bullish BOS with aligned displacement — breakout confirmation');
  }
  if (s.sweepReclaim && s.smcBias === 'bullish') {
    score += 20;
    rationale.push('Swept liquidity reclaimed from below — long setup valid');
  }
  if (s.aboveVWAP) {
    score += 10;
    rationale.push('Price above VWAP — long bias confirmed');
  }
  if (!s.atResistance && !s.hasEqualHighs) {
    score += 15;
    rationale.push('Clean above resistance — no immediate overhead liquidity conflict');
  }

  return { score: Math.min(100, score), bias: 'long', rationale };
}

function ruleBreakdownConfirmedShort(s: SignalSummary): RuleResult {
  if (s.smcBias !== 'bearish' || s.trapSeverity === 'High') return { score: 0, bias: 'short', rationale: [] };
  if (!s.hasBOS || !s.bosFresh) return { score: 0, bias: 'short', rationale: [] };

  let score = 0;
  const rationale: string[] = [];

  if (s.bosFresh && s.dispDirection === 'bearish' && s.dispFresh) {
    score += 60;
    rationale.push('Fresh bearish BOS with aligned displacement — breakdown confirmation');
  }
  if (s.sweepReclaim && s.smcBias === 'bearish') {
    score += 20;
    rationale.push('Swept liquidity reclaimed from above — short setup valid');
  }
  if (!s.aboveVWAP) {
    score += 10;
    rationale.push('Price below VWAP — short bias confirmed');
  }
  if (!s.atSupport && !s.hasEqualLows) {
    score += 15;
    rationale.push('Clean below support — no immediate underfoot liquidity conflict');
  }

  return { score: Math.min(100, score), bias: 'short', rationale };
}

function rulePullbackToSupportPending(s: SignalSummary): RuleResult {
  // Bullish bias, setup is late (above VWAP but not at trigger), wait for retrace
  if (s.smcBias !== 'bullish') return { score: 0, bias: 'long', rationale: [] };
  if (s.atResistance || s.hasEqualHighs) return { score: 0, bias: 'long', rationale: [] }; // compression takes priority

  let score = 0;
  const rationale: string[] = [];

  if (s.hasBOS && s.aboveVWAP && !s.bosFresh) {
    score += 45;
    rationale.push('Bullish structure confirmed but BOS is aged — wait for pullback to support');
  }
  if (s.vwapDistPct > 0.008) {
    score += 20;
    rationale.push(`Price ${(s.vwapDistPct * 100).toFixed(1)}% from VWAP — late entry, wait for retrace`);
  }
  if (s.regimeDominant === 'trend' && s.trendDirection === 'bullish') {
    score += 15;
    rationale.push('Bullish trend intact — pullback entry preferred over chasing');
  }

  return { score: Math.min(100, score), bias: 'long', rationale };
}

function rulePullbackToResistancePending(s: SignalSummary): RuleResult {
  if (s.smcBias !== 'bearish') return { score: 0, bias: 'short', rationale: [] };
  if (s.atSupport || s.hasEqualLows) return { score: 0, bias: 'short', rationale: [] };

  let score = 0;
  const rationale: string[] = [];

  if (s.hasBOS && !s.aboveVWAP && !s.bosFresh) {
    score += 45;
    rationale.push('Bearish structure confirmed but BOS is aged — wait for bounce to resistance');
  }
  if (s.vwapDistPct > 0.008) {
    score += 20;
    rationale.push(`Price ${(s.vwapDistPct * 100).toFixed(1)}% from VWAP — late entry, wait for retrace`);
  }
  if (s.regimeDominant === 'trend' && s.trendDirection === 'bearish') {
    score += 15;
    rationale.push('Bearish trend intact — bounce entry preferred over chasing');
  }

  return { score: Math.min(100, score), bias: 'short', rationale };
}

function ruleTrendContinuationActive(s: SignalSummary): RuleResult {
  // Clean continuation — trend active, displacement aligned, no traps, no resistance overhead.
  //
  // INTENTIONAL DESIGN: PULLBACK_TO_SUPPORT/RESISTANCE_PENDING (bucket 3) outranks
  // TREND_CONTINUATION_ACTIVE (bucket 4) when the BOS is aged or price is extended
  // from VWAP. This is deliberate — late entry into a mature move is structurally
  // inferior to waiting for a pullback even when all three trend layers agree.
  // Do NOT "correct" this by elevating continuation to bucket 3.
  if (s.trapSeverity === 'High' || s.trapSeverity === 'Medium') return { score: 0, bias: 'neutral', rationale: [] };
  if (s.atResistance || s.atSupport) return { score: 0, bias: 'neutral', rationale: [] };
  if (s.extensionPct >= 0.025) return { score: 0, bias: 'neutral', rationale: [] }; // too extended

  const bullCont = s.smcBias === 'bullish' && s.trendDirection === 'bullish' && s.dispDirection === 'bullish';
  const bearCont = s.smcBias === 'bearish' && s.trendDirection === 'bearish' && s.dispDirection === 'bearish';

  if (!bullCont && !bearCont) return { score: 0, bias: 'neutral', rationale: [] };

  let score = 0;
  const rationale: string[] = [];
  const bias: DirectionalBias = bullCont ? 'long' : 'short';

  if (s.dispFresh && s.dispStrength >= 0.4) {
    score += 45;
    rationale.push('Fresh displacement aligned with structure bias and trend');
  }
  if (s.bosFresh) {
    score += 25;
    rationale.push('Recent BOS confirms structural continuation');
  }
  if (s.trendStrength >= 0.5) {
    score += 15;
    rationale.push(`Trend strength ${(s.trendStrength * 100).toFixed(0)}% — continuation valid`);
  }
  if (s.regimeDominant === 'trend') {
    score += 15;
    rationale.push('Trend regime dominant — continuation favored');
  }

  return { score: Math.min(100, score), bias, rationale };
}

function ruleRangeRotation(s: SignalSummary): RuleResult {
  // RANGE_ROTATION = defined sideways structure: regime identifies a range, direction absent.
  // Distinct from NO_VALID_SETUP: range IS the signal here — price is bounded and rotating.
  // NO_VALID_SETUP = lack of structure clarity: signals conflict or are insufficient entirely.
  // Regime is meanReversion/noTrade, price bounded, no clear direction
  const ranging = s.regimeDominant === 'meanReversion' || s.regimeDominant === 'noTrade';
  const noClarity = s.smcBias === 'neutral' || s.biasStrength === 'minor';
  const bounded   = !s.dispFresh && !s.bosFresh;

  if (!ranging && (!noClarity || !bounded)) return { score: 0, bias: 'neutral', rationale: [] };

  let score = 0;
  const rationale: string[] = [];

  if (ranging) {
    score += 40;
    rationale.push(`${s.regimeDominant} regime — range-bound environment`);
  }
  if (noClarity) {
    score += 25;
    rationale.push('Neutral/weak SMC bias — no directional edge');
  }
  if (bounded) {
    score += 20;
    rationale.push('No fresh BOS or displacement — rotational action');
  }

  return { score: Math.min(100, score), bias: 'neutral', rationale };
}

function ruleNoValidSetup(s: SignalSummary, composite: number): RuleResult {
  // NO_VALID_SETUP = final fallback when composite score is below minimum threshold.
  // Signals are conflicting or insufficient — no structure, no range, no edge.
  // If a defined range exists (even sideways), RANGE_ROTATION is the correct state.
  if (composite >= PENDING_MIN_COMPOSITE) return { score: 0, bias: 'neutral', rationale: [] };

  const rationale: string[] = [];
  if (composite < 20) rationale.push('Insufficient structural signals for any setup classification');
  else rationale.push('Conflicting signals — no clean action path available');
  if (s.trapSeverity !== 'None') rationale.push(`Active ${s.trapSeverity} trap further reduces clarity`);

  return { score: 80, bias: 'neutral', rationale };
}

// ─── Rule registry and selection ─────────────────────────────────────────────

interface StateCandidate {
  state:     MarketState;
  score:     number;
  bias:      DirectionalBias;
  rationale: string[];
}

// ─── Priority buckets for deterministic tie-breaking ─────────────────────────
//
// When two states score equally, the bucket order resolves the tie.
// This is the authoritative precedence table — inspectable and auditable.
//
//  Bucket 1 (override):  TRAP_REVERSAL_RISK, EXHAUSTION_RISK
//    → Always win when active. Trap and exhaustion represent risk-first conditions
//      that must override any continuation or breakout signal.
//
//  Bucket 2 (confirmed): BREAKOUT_CONFIRMED_LONG, BREAKDOWN_CONFIRMED_SHORT
//    → Confirmed directional events with acceptance beat generic continuation.
//      These are specific confirmed outcomes, not mere directional bias.
//
//  Bucket 3 (pending):   COMPRESSION_BREAKOUT_PENDING, PULLBACK_TO_SUPPORT_PENDING,
//                         PULLBACK_TO_RESISTANCE_PENDING
//    → Wait states. Compression beats pullback when equal highs/lows are present.
//      Pullback states beat Range or No-Valid when structure exists but entry timing is wrong.
//
//  Bucket 4 (trend):     TREND_CONTINUATION_ACTIVE
//    → Active continuation. Loses to confirmed breakout (which is more specific)
//      but beats range when all three layers (SMC + trend + displacement) agree.
//
//  Bucket 5 (fallback):  RANGE_ROTATION, NO_VALID_SETUP
//    → Default states. RANGE_ROTATION beats NO_VALID_SETUP when regime is clear
//      even if direction is not. NO_VALID_SETUP is the final fallback.

const STATE_PRIORITY_BUCKET: Record<import('./types').MarketState, number> = {
  TRAP_REVERSAL_RISK:              1,
  EXHAUSTION_RISK:                 1,
  BREAKOUT_CONFIRMED_LONG:         2,
  BREAKDOWN_CONFIRMED_SHORT:       2,
  COMPRESSION_BREAKOUT_PENDING:    3,
  PULLBACK_TO_SUPPORT_PENDING:     3,
  PULLBACK_TO_RESISTANCE_PENDING:  3,
  TREND_CONTINUATION_ACTIVE:       4,
  RANGE_ROTATION:                  5,
  NO_VALID_SETUP:                  5,
};

export function evaluateAllRules(
  s:         SignalSummary,
  scoreFactors: StateScoreFactors,
): StateCandidate[] {
  const c = scoreFactors.composite;

  const candidates: StateCandidate[] = [
    { state: 'TRAP_REVERSAL_RISK',                 ...ruleTrapReversalRisk(s)              },
    { state: 'EXHAUSTION_RISK',                    ...ruleExhaustionRisk(s)                },
    { state: 'COMPRESSION_BREAKOUT_PENDING',        ...ruleCompressionBreakoutPending(s)    },
    { state: 'BREAKOUT_CONFIRMED_LONG',             ...ruleBreakoutConfirmedLong(s)         },
    { state: 'BREAKDOWN_CONFIRMED_SHORT',           ...ruleBreakdownConfirmedShort(s)       },
    { state: 'TREND_CONTINUATION_ACTIVE',           ...ruleTrendContinuationActive(s)       },
    { state: 'PULLBACK_TO_SUPPORT_PENDING',         ...rulePullbackToSupportPending(s)      },
    { state: 'PULLBACK_TO_RESISTANCE_PENDING',      ...rulePullbackToResistancePending(s)   },
    { state: 'RANGE_ROTATION',                      ...ruleRangeRotation(s)                },
    { state: 'NO_VALID_SETUP',                      ...ruleNoValidSetup(s, c)               },
  ];

  // Filter to active rules (score > 0), then sort by:
  //   Primary:   priority bucket (ascending — bucket 1 wins)
  //   Secondary: score (descending — higher score wins within same bucket)
  // This makes tie-breaking deterministic and auditable.
  return candidates
    .filter(c => c.score > 0)
    .sort((a, b) => {
      const pa = STATE_PRIORITY_BUCKET[a.state];
      const pb = STATE_PRIORITY_BUCKET[b.state];
      if (pa !== pb) return pa - pb;   // lower bucket number = higher priority
      return b.score - a.score;        // higher score wins within same bucket
    });
}

export function selectPrimaryState(candidates: StateCandidate[]): StateCandidate {
  if (!candidates.length) {
    return { state: 'NO_VALID_SETUP', score: 0, bias: 'neutral', rationale: ['No rules matched'] };
  }
  // candidates[0] is always the deterministic winner after bucket+score sort
  return candidates[0];
}

/** Export priority table for audit and testing */
export { STATE_PRIORITY_BUCKET };
